import { Http } from '@/lib/http';

export class MemberProfileService extends Http {
  constructor() {
    super();
    this.endpoint = 'aml-pro/app/person-management/';
  }

  /**
   * Search for members based on filter criteria
   * @param {Object} filters - Search filters
   * @param {Number} offset - Pagination offset
   * @param {Number} limit - Pagination limit
   * @returns {Promise}
   */
  searchMembers(filters, offset = 0, limit = 25) {
    return this.httpClient().post(`${this.endpoint}search`, filters, {
      params: { offset, limit },
    });
  }

  /**
   * Fetch all user-defined KYC risk levels (used to build colour map on page load)
   * @returns {Promise}
   */
  getKycLevels() {
    return this.httpClient().get(`${this.endpoint}kyc-levels`);
  }

  /**
   * Get a member by ID
   * @param {String} id
   * @returns {Promise}
   */
  getMemberById(id) {
    return this.httpClient().get(`${this.endpoint}${id}`);
  }

  /**
   * Update member KYC score
   * @param {String} id
   * @param {String} userId - Acting user ID
   * @param {Object} kycScore - KYC score data
   * @returns {Promise}
   */
  updateKycScore(id, userId, kycScore) {
    return this.httpClient().patch(`${this.endpoint}${id}/${userId}/kyc-score`, { kycScore });
  }

  /**
   * Update member watchlists
   * @param {String} id
   * @param {Object} watchlists
   * @returns {Promise}
   */
  updateWatchlists(id, watchlists) {
    return this.httpClient().patch(`${this.endpoint}${id}/watchlists`, { watchlists });
  }

  /**
   * Update account trends for a member
   * @param {String} id
   * @param {Array} trendsData
   * @returns {Promise}
   */
  updateAccountTrends(id, trendsData) {
    return this.httpClient().patch(`${this.endpoint}${id}/trends`, trendsData);
  }

  /**
   * Aggregate stats for the Mass Processing Dashboard.
   * Returns KYC risk distribution + trends coverage in a single call.
   *
   * @returns {Promise<{
   *   data: {
   *     totalPersons:          number,
   *     kycByLevel:            Record<string, number>,
   *     totalAccounts:         number,
   *     accountsWithTrends:    number,
   *     accountsWithoutTrends: number
   *   }
   * }>}
   */
  fetchDashboardStats() {
    return this.httpClient().get(`${this.endpoint}stats/dashboard`);
  }

  /**
   * Start a mass KYC scoring job — scoring only, no trend application.
   * @param {string[]} personIds 
   * @param {Object|null} filters
   */
  startMassKycScoring(personIds = [], filters = null) {
    return this.httpClient().post(`${this.endpoint}mass-kyc-scoring`, { personIds, filters });
  }

  /**
   * Start a bulk job that rescores every person with a fresh KYC score
   * then writes matching global trend thresholds into their accounts.
   *
   * @param {Boolean} overwrite
   * @param {string[]} personIds
   * @param {Object|null} filters 
   *
   * @returns {Promise<{ jobId: string }>}
   */
  applyGlobalTrends(overwrite, personIds = [], filters = null) {
    return this.httpClient().post(`${this.endpoint}apply-trends`, { overwrite, personIds, filters });
  }

  /**
   * Poll the progress of a running apply trends job.
   *
   * @param {String} jobId
   * @returns {Promise<{
   *   jobId: string,
   *   total: number,
   *   processed: number,
   *   skipped: number,
   *   failed: number,
   *   percent: number,
   *   status: 'running' | 'done' | 'error',
   *   message: string
   * }>}
   */
  getApplyTrendsProgress(jobId) {
    return this.httpClient().get(`${this.endpoint}apply-trends/progress/${jobId}`);
  }
}